import { Injectable, inject } from '@angular/core';

import { datadogRum } from '@datadog/browser-rum';
import type { RumPublicApi } from '@datadog/browser-rum-core';
import { Observable, ReplaySubject, filter, take } from 'rxjs';

import { CookieService } from '../browser/cookie/cookie.service';
import { WINDOW } from '../browser/window/window.token';
import { HA_APP_KEY } from '../client-config/client-config.model';
import { CLIENT_CONFIG_TOKEN } from '../client-config/client-config.token';
import { DslService } from '../dsl/dsl.service';
import { Logger } from '../logging/logger';
import { UserLoginEvent } from '../user/user-events';
import { UserService } from '../user/user.service';
import { DatadogRumConfig } from './datadog-rum.client-config';

@Injectable({
    providedIn: 'root',
})
export class DatadogRumService {
    private readonly userService = inject(UserService);
    private readonly cookieService = inject(CookieService);
    private readonly clientConfig = inject(CLIENT_CONFIG_TOKEN);
    private readonly datadogRumConfig = inject(DatadogRumConfig);
    private readonly dslService = inject(DslService);
    private readonly logger = inject(Logger);
    readonly #window = inject(WINDOW);

    private initialized = false;

    private readonly defaultConfig: any = {
        version: this.cookieService.get('c-a-v') || this.#window['VERSION'] || 'unknown',
    };

    private _sdkInitialized$ = new ReplaySubject<void>(1);

    /**
     * The Datadog RUM SDK instance. Returns null if not initialized.
     * Use this to call other Datadog RUM methods that are not exposed in the {@link DatadogRumService} class.
     * Do not call init() method on this instance as the bootstrapper will handle that.
     */
    get sdkInitialized$(): Observable<void> {
        return this._sdkInitialized$.pipe(take(1));
    }

    /**
     * The Datadog RUM SDK instance. Returns null if not initialized, if you need to check if the SDK is initialized, use {@link sdkInitialized$}.
     * Use this to call other Datadog RUM methods like addError, addAction, etc.
     */
    get datadogRumSdk(): RumPublicApi | null {
        return this.initialized ? datadogRum : null;
    }

    /** @internal */
    init(): void {
        if (this.initialized) return;

        try {
            if (this.datadogRumConfig.initOptions.allowedTracingUrls) {
                this.datadogRumConfig.initOptions.allowedTracingUrls = this.datadogRumConfig.initOptions.allowedTracingUrls.map((value) =>
                    this.parseValue(value.toString()),
                );
            }
            const options = Object.assign({}, this.defaultConfig, this.datadogRumConfig.initOptions);
            datadogRum.init(options);
            this.initialized = true;
            this._sdkInitialized$.next();

            this.setUserContext();
            this.setGlobalContextProperties(this.datadogRumConfig.globalContextProperties);
        } catch (error) {
            this.logger.errorRemote('Error initializing Datadog RUM', error);
        }
    }

    private setGlobalContextProperties(properties: Record<string, string>): void {
        try {
            Object.entries(properties).forEach(([key, value]) => {
                this.dslService.evaluateExpression<any>(value).subscribe((evaluatedValue) => {
                    datadogRum.setGlobalContextProperty(key, evaluatedValue);
                });
            });
        } catch (error) {
            this.logger.errorRemote('Error setting Datadog RUM global context properties', error);
        }
    }

    private setUserContext() {
        const correlationId = this.clientConfig[HA_APP_KEY]?.correlationId;
        datadogRum.setUser({
            id: correlationId,
        });

        if (this.userService.isAuthenticated) {
            datadogRum.setUserProperty('name', this.userService.id!);
        }
        this.userService.events.pipe(filter((event) => event instanceof UserLoginEvent)).subscribe(() => {
            datadogRum.setUserProperty('name', this.userService.id!);
        });
    }

    private isRegexString(value: string): boolean {
        return /^\/.*\/[gimsuy]*$/.test(value);
    }
    private isFunctionString(value: string): boolean {
        return /^\s*(function|\(.*\)\s*=>)/.test(value);
    }
    private parseValue(value: string): string | RegExp | ((url: string) => boolean) {
        if (this.isRegexString(value)) {
            try {
                const parts = value.split('/');
                const pattern = parts.slice(1, -1).join('/'); // handles escaped slashes
                const flags = parts[parts.length - 1];
                return new RegExp(pattern, flags);
            } catch (error) {
                this.logger.errorRemote(`Error parsing regex string from value: ${value}`, error);
                return value;
            }
        }

        if (this.isFunctionString(value)) {
            return new Function('return ' + value)() as (url: string) => boolean;
        }
        return value;
    }
}
